import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { NotificationManager } from 'react-notifications';
import BllinkUtils from '../../../components/common/utils/utils';
import BllinkTime from '../../../components/common/utils/time';
import { isAdminSelector } from '../../../components/store/auth/selectors';
import { showLoader, hideLoader } from '../../../components/store/common/slice';
import { constants } from '../../../components/common/constants/constants';
import { selectCustomStyles } from '../../../components/common/shared/select.styled';
import { calcMonthsTextForSelectBox } from '../../../components/common/shared/statelessMethods';
import { StyledSearchButton } from '../SearchForm/SearchForm.styled';
import PlanActionsRadioGroup from './PlanActionsRadioGroup/PlanActionsRadioGroup';
import { StyledPlanActionsWrapper } from './PlanActions.styled';
import {useAccessToken} from '../../../components/common/hooks/useAccessToken';
import { StyledHeader } from '../../components/AdminPagesWrapper/AdminPagesWrapper.styled';

const PlanActions = () => {
    const dispatch = useDispatch();
    const client = useAccessToken();
    const isAdmin = useSelector(isAdminSelector);
    const { t } = useTranslation();
    const { cashServerKey, wireServerKey, chequeServerKey } = constants.managerPaymentsOptions;
    const [data, setData] = useState({
        planId: null,
        isOffline: null,
        address: '',
        planStatus: '',
        months: '',
        payments: [],
        buildingId: '',
        isFinishedPlan: null,
        isPtopPlan: null,
        isOneTimePlan: null,
        lastPlanMonth: null,
        lastPlanYear: null,
        firstPendingMonth: null,
        firstPendingYear: null
    });
    const [showPayments, setShowPayments] = useState(false);
    const [radiosDisabled, setRadiosDisabled] = useState({
        extendDisabled: false,
        shortenDisabled: false,
        stopAllDisabled: false,
        renewDisabled: false,
        switchCardDisabled: false
    });
    const [creditCards, setCreditCards] = useState([]);
    const [dataToSend, setDataToSend] = useState({
        radio: '',
        dropdown: ''
    });

    const transformPaymentsToArray = installments => {
        let paymentsArr = installments.map(installment => `${installment.amount} ₪, ${t('searchPayments.paidAt')} ${BllinkUtils.BllinkTime.parseDate(installment.dateToCharge)}, ${t('searchPayments.forMonths')} ${calcMonthsTextForSelectBox(installment.months)}`);
        return paymentsArr;
    };

    const getFirstAndLastPendingMonth = (installmentsPlan = [], status) => {
        const pendingPaymentDetails = installmentsPlan.filter(month => month.status === status);
        const pendingMonths = pendingPaymentDetails.flatMap(ppd=> {return ppd.months});
        const pendingMonthsSorted = pendingMonths.sort((monthA, monthB) => {return BllinkUtils.BllinkTime.isSmallerThan(monthA, monthB) ? -1 : 1});
        const firstPendingMonth = pendingMonthsSorted[0];
        const lastPendingMonth = pendingMonthsSorted[pendingMonthsSorted.length-1];
        return {firstPendingMonth, lastPendingMonth};

    }

    const getPlanId = () => {
        const pathArr = window.location.pathname.split('/');
        const id = pathArr[pathArr.length-1];
        setData({
            ...data,
            planId: id
        });
    };

    const fetchData = async () => {     
        try {
            let response = await client.get(`/tenants/order/${data.planId}`, { includeData: true });
            let installment;
            
            const { onGoingInstallments, oneTimePaymentsInstallments, pastPaymentsInstallments } = response.installments_details;
    
            if (onGoingInstallments) installment = onGoingInstallments[0]
            else if (oneTimePaymentsInstallments) installment = oneTimePaymentsInstallments[0]
            else if (pastPaymentsInstallments) installment = pastPaymentsInstallments[0]
    
    
            const {
                selectedMonths,
                installmentsPlan,
                isOffline,
                isFinishedPlan,
                isPtopPlan,
                number_of_installments,
                lastPaymentId,
                CreatedBy,
                comment,
                createdAt
            } = installment;
    
            const {firstPendingMonth, lastPendingMonth} =  getFirstAndLastPendingMonth(installmentsPlan, constants.PaymentPlanDetailsStatus.PENDING);
    
            const createdBy = CreatedBy || t('searchPayments.planActionSubheaders.createdByDefault');
    
            const apartmentDetails = response.apartment_details; // in case of payment
            const buildingDetails = response.building_payer_details; // in case of expense
            const address = apartmentDetails ? apartmentDetails.title : buildingDetails.name;
            const buildingID = apartmentDetails ? apartmentDetails.buildingID : response.buildingID;
            const payedTo = apartmentDetails ? apartmentDetails.apartmentNumber: response.vendor_details.title;
    
            setData({
                ...data,
                address: address,
                buildingID: buildingID,
                apartmentNumber: payedTo,
                isOffline: isOffline,
                planStatus: installment.overAllStatus,
                months: selectedMonths ? calcMonthsTextForSelectBox(selectedMonths) : null,
                payments: installmentsPlan ? transformPaymentsToArray(installmentsPlan) : null,
                isFinishedPlan: isFinishedPlan,
                isPtopPlan: isPtopPlan,
                lastPaymentId: lastPaymentId,
                numberOfInstallments: number_of_installments,
                isOneTimePlan: !selectedMonths,
                lastPlanMonth: lastPendingMonth?.month,
                lastPlanYear: lastPendingMonth?.year,
                firstPendingMonth: firstPendingMonth?.month,
                firstPendingYear: firstPendingMonth?.year,
                createdBy,
                comment,
                createdAt
            });
        } catch (e) {
            return;
        }
    };

    const disableNecessaryRadioButtons = (isFinished, isOneTime, PtoP) => {
        if (isFinished) {
            setRadiosDisabled({
                extendDisabled: false,
                shortenDisabled: true,
                stopAllDisabled: true,
                renewDisabled: false,
                switchCardDisabled: true
            })
        } else if (PtoP) {
            setRadiosDisabled({
                extendDisabled: true,
                shortenDisabled: true,
                stopAllDisabled: false,
                renewDisabled: true,
                switchCardDisabled: false
            })
        } else if (!isOneTime) {
            setRadiosDisabled({
                extendDisabled: false,
                shortenDisabled: false,
                stopAllDisabled: false,
                renewDisabled: true,
                switchCardDisabled: false
            })
        } else if (isOneTime) {
            setRadiosDisabled({
                extendDisabled: true,
                shortenDisabled: true,
                stopAllDisabled: true,
                renewDisabled: true,
                switchCardDisabled: false
            })
        }
    };

    const getCreditCardsDigits = async () => {
        const response = await client.get(`/managers/operations/apartmentDetails/${data.buildingID}/${data.address.apartmentNumber}`);
        let cards = response?.lastCreditCards4Digits;

        const mapByPaymentId = cards => {
            let cardsMap = {};

            for (const card of cards){
                cardsMap[card.paymentID] = card
            }

            return cardsMap
        };

        setData({
            ...data,
            creditCardsData: cards,
            cardsByPaymentId: mapByPaymentId(cards || {})
        });
    };

    useEffect(() => {
        if (data.creditCardsData) {
            if (data.creditCardsData?.length !== 0) {
                setData({
                    ...data,
                    paymentID: data.creditCardsData[0].paymentID
                })
            }

            const cardsDropdownOptions = data.creditCardsData.map((card, i) => {
                return {
                    value: { last4Digits: card.lastCreditCards4Digits },
                    label: card.lastCreditCards4Digits,
                    index: i
                }
            });
            setCreditCards([
                ...cardsDropdownOptions,
                {
                    value: 'newCard',
                    label: t('searchPayments.newCard'),
                    index: 'add'
                }
            ]);
        }
    }, [data.creditCardsData])

    useEffect(() => {
        (async function () {
            dispatch(showLoader());

            getPlanId();

            dispatch(hideLoader());
        })();
    }, []);

    useEffect(() => {
        (async function () {
            if (!client) return;
            dispatch(showLoader());

            if (data.planId) await fetchData()

            dispatch(hideLoader());
        })();
    }, [data.planId, client]);

    useEffect(() => {
        const { isFinishedPlan, isOneTimePlan, isPtopPlan } = data;
        if (isFinishedPlan !== null) {
            disableNecessaryRadioButtons(isFinishedPlan, isOneTimePlan, isPtopPlan);
            getCreditCardsDigits();
        }
    }, [data.isFinishedPlan]);

    //dropdowns
    let extendDropdownOptions = [];
    let shortenOptions = [];
    let renewOptions = [];

    //gets 12 months after specified month not including start month
    const getNextTwelveMonths = (optionsArr, startMonth, startYear) => {
        let options = [];
        let current = {
            month: +startMonth,
            year: +startYear
        };
        for (let i = 0; i < 12; i++) {
            const nextMonth = BllinkTime.getNextMonth(current);
            options.push(nextMonth);
            current = options[options.length-1];
        }
        for (let i = 0, max = options.length; i < max; i++) {
            const option = options[i];
            optionsArr.push({
                value: {month: option.month, year: option.year},
                label: `${constants.MonthsToText[option.month]} ${option.year}`,
                index: i
            });
        }
    };

    //gets all months between two including start and end month
    const getMonthsBetween = (optionsArr, startMonth, startYear, endMonth, endYear) => {
        let options = [];
        let current = {
            month: +startMonth - 1,
            year: +startYear
        };
        let endDate = {
            month: +endMonth,
            year: +endYear
        };
        while (JSON.stringify(current) !== JSON.stringify(endDate)) {
            const nextMonth = BllinkTime.getNextMonth(current);
            options.push(nextMonth);
            current = options[options.length-1];
        }
        for (let i = 0, max = options.length; i < max; i++) {
            const option = options[i];
            optionsArr.push({
                value: {month: option.month, year: option.year},
                label: `${constants.MonthsToText[option.month]} ${option.year}`,
                index: i
            })
        }
    }

    const calculateOptionsForExtendDropdown = () => {
        if (data.lastPlanMonth) {
            getNextTwelveMonths(extendDropdownOptions, data.lastPlanMonth, data.lastPlanYear);
        }
    };

    const calculateOptionsForShortenDropdown = () => {
        if (data.firstPendingMonth) {
            getMonthsBetween(shortenOptions, data.firstPendingMonth, data.firstPendingYear, data.lastPlanMonth, data.lastPlanYear)
        }
    };

    const calculateOptionsForRenewDropdown = () => {
        const date = new Date();
        const currentMonth = date.getMonth();
        const currentYear = date.getFullYear();
        getNextTwelveMonths(renewOptions, currentMonth, currentYear)
    };

    calculateOptionsForExtendDropdown();
    calculateOptionsForShortenDropdown();
    calculateOptionsForRenewDropdown();

    const showOrHidePaymentsBlock = () => setShowPayments(!showPayments);

    const onRadioChange = e => {
        setDataToSend({
            radio: e.target.value
        })
    };

    const onSelectChange = value => {
        setDataToSend({
            ...dataToSend,
            dropdown: value
        })
    };

    const paymentTypes = [
        {
            value: cashServerKey,
            label: t(`buildings.${cashServerKey}`)
        },
        {
            value: chequeServerKey,
            label: t(`buildings.${chequeServerKey}`)
        },
        {
            value: wireServerKey,
            label: t(`buildings.${wireServerKey}`)
        }
    ];

    const success = message => {
        NotificationManager.success(`${t('searchPayments.notifications.planWasSuccessfully')} ${message}`);
        setTimeout(() => window.location.reload(), 2000);
    };

    const failure = () => NotificationManager.error(t('searchPayments.notifications.failure'));

    const chooseDateError = () => NotificationManager.error(t('searchPayments.notifications.chooseDate'));

    const sendData = async () => {
        const { radio, dropdown } = dataToSend;
        const id = data.planId;
        switch (radio) {
            case 'extend':
                if (dropdown?.month) {
                    let extendPayload;
                    if (data.isPtopPlan) {
                        extendPayload = {
                            PaymentPlanID: id,
                            itemsToAdd: +data.lastPlanMonth - +dropdown.month
                        };
                    } else {
                        extendPayload = {
                            PaymentPlanID: id,
                            extendTill: dropdown
                        };
                    }
                    dispatch(showLoader());
                    const extendResponse = await client.post(`/operations/extend-plan/`, extendPayload);
                    if (extendResponse?.numberOfNewPlanDetails > 0) success(t('searchPayments.notifications.extended'))
                    else failure();
                    dispatch(hideLoader());
                } else chooseDateError();
                break;

            case 'shorten':
                if (dropdown?.month) {
                    dispatch(showLoader());
                    const shortenResponse = await client.managers.operations.shortenPlan(data.buildingID, id, {
                        stopFrom: { month: dropdown.month, year: dropdown.year },
                        allPending: false,
                    });
                    if (shortenResponse?.result?.stopped?.length > 0) success(t('searchPayments.notifications.shortened'))
                    else failure();
                    dispatch(hideLoader());
                } else chooseDateError();
                break;

            case 'stopAll':
                dispatch(showLoader());
                const stopAllResponse = await client.managers.operations.shortenPlan(data.buildingID, id, { allPending: true });
                if (stopAllResponse?.result?.stopped?.length > 0) success(t('searchPayments.notifications.stopped'))
                else failure();
                dispatch(hideLoader());
                break;

            case 'renew':
                if (dropdown?.month) {
                    const date = new Date();
                    const currentMonth = +date.getMonth() + 1;
                    const nextMonth = +dropdown.month;
                    let monthsForward;
                    const calculateMonthsForward = () => {
                        if (currentMonth < nextMonth) monthsForward = nextMonth - currentMonth
                        else monthsForward = 12 - (currentMonth - nextMonth)
                    };
                    calculateMonthsForward();

                    const renewPayload = {
                        lastPaymentId: data.lastPaymentId,
                        number_of_months_forward: monthsForward,
                        number_of_installments: data.numberOfInstallments,
                        send_invoice: true,
                        send_confirmation: true
                    };
                    dispatch(showLoader());
                    const renewResponse = await client.post(`/operations/renew-plan/`, renewPayload);
                    if (renewResponse?.newPlanID) success(t('searchPayments.notifications.renewed'))
                    else failure();
                    dispatch(hideLoader());
                } else chooseDateError();
                break;

            case 'switchCreditCard':
                if (dropdown) {
                    if (dropdown === 'newCard') {
                        const payload = {
                            functionName: "GetPaymentLink",
                            PaymentPlanID: id
                        };
                        dispatch(showLoader());
                        const newCardResponse = await client.post(`/callbacks/PtopGateway`, payload);
                        let isUrl = BllinkUtils.JsExtras.validURL(newCardResponse)
                        if (isUrl) {
                            window.open(newCardResponse)
                        }
                        dispatch(hideLoader());
                    } else {
                        const payload = {
                            PaymentPlanID: data.planId,
                            paymentID: id,
                            last4Digits: dropdown.last4Digits
                        };
                        dispatch(showLoader());
                        const chargeCardResponse = await client.post('/managers/operations/charge-cart/', payload);
                        if (chargeCardResponse?.success) success(t('searchPayments.notifications.cardCharged'))
                        else failure();
                        dispatch(hideLoader());
                    }
                } else NotificationManager.error(t('searchPayments.notifications.chooseCard'))
                break;
            default: return
        }
    };

    if (!isAdmin) return <div>{t('searchPayments.noAdminRights')}</div>;

    return (
        <StyledPlanActionsWrapper>
            <StyledHeader>{[t('searchPayments.planActionsHeaders.onlineHeader'), ` ${data.planId}`]}</StyledHeader>
            <div> {t('searchPayments.planActionSubheaders.buildingAddress')} <a href={`/${constants.pages.newApartmentReport}/${data.buildingID}/${data.apartmentNumber}`}> <strong>{data.address} , {t('searchPayments.tableHeaders.aptNumber')} {data.apartmentNumber}</strong></a></div>
            <div>{t('searchPayments.planActionSubheaders.planCreatedBy')} <strong>{data.createdBy}</strong> </div>
            {data.comment && <div>{t('searchPayments.tableHeaders.planComment')} <strong>{data.comment}</strong></div>}
            {data.createdAt && <div>{t('searchPayments.tableHeaders.createdAt')} <strong>{BllinkTime.parseDate(data.createdAt, {includeTime: true})}</strong></div>}
            {!data.isOffline ? (
                <>
                    <div>{t('searchPayments.planActionSubheaders.planStatus')} <strong>{data.planStatus ? t(`statuses.${data.planStatus}`): ''}</strong></div>
                    {!data.isOneTimePlan && (
                        <>
                            <div>{t('searchPayments.planActionSubheaders.planMonths')} <strong>{data.months}</strong></div>
                            <div className='payments-wrapper'>
                                <div onClick={() => showOrHidePaymentsBlock()}>{t('searchPayments.planActionSubheaders.payments')}</div>
                                <strong className={`payments ${showPayments && 'show'}`}>{data.payments && data.payments.map((item, i) => <div key={i}>{item}</div>)}</strong>
                            </div>
                        </>
                    )}

                    <PlanActionsRadioGroup
                        radiosDisabled={radiosDisabled}
                        extendDropdownOptions={extendDropdownOptions}
                        shortenDropdownOptions={shortenOptions}
                        renewDropdownOptions={renewOptions}
                        creditCardsOptions={creditCards}
                        onChange={onRadioChange}
                        onSelectChange={onSelectChange}
                    />
                </>
            ) : (
                <div>
                    {t('searchPayments.planActionsHeaders.offlineHeader')}
                    <Select
                        styles={selectCustomStyles}
                        options={paymentTypes || []}
                        onChange={e => onSelectChange(e, 'offlinePaymentMethod')}
                    />
                </div>
            )}
            <StyledSearchButton onClick={sendData}>{t('searchPayments.btns.send')}</StyledSearchButton>
        </StyledPlanActionsWrapper>
    );
};

export default PlanActions;
